import type { Participant } from '#src/pages/create/types.ts';
import { useMemo, useState } from 'react';

export const MINIMUM_PARTICIPANTS = 3;
export const MAXIMUM_PARTICIPANTS = 150;

export function useParticipantsList() {
  const [participants, setParticipants] = useState<Participant[]>(() => initParticipants());
  const [error, setError] = useState<string | undefined>(undefined);
  const [focusParticipantId, setFocusParticipantId] = useState<string | undefined>(
    participants[0]?.id,
  );

  const isParticipantsEmpty = useMemo(
    () => participants.filter(isNonEmptyParticipant).length === 0,
    [participants],
  );

  const duplicateParticipantIds = useMemo(
    () => getDuplicateParticipantIds(participants.filter(isNonEmptyParticipant)),
    [participants],
  );

  /**
   * Validate the participants list.
   * Returns false if the list is invalid, otherwise true.
   * Sets the error state if the list is invalid.
   */
  const validateParticipants = () => {
    // Only check participants with a name.
    const nonEmpty = participants.filter(isNonEmptyParticipant);

    // Check minimum number of participants.
    if (nonEmpty.length < MINIMUM_PARTICIPANTS) {
      setError(`You must add at least ${MINIMUM_PARTICIPANTS} names.`);
      return false;
    }

    // Check for duplicate names.
    if (duplicateParticipantIds.size > 0) {
      setError('All names must be unique. Duplicate names have been highlighted in the list.');
      return false;
    }

    // Check maximum number of participants.
    if (nonEmpty.length > MAXIMUM_PARTICIPANTS) {
      setError(`You can only add up to ${MAXIMUM_PARTICIPANTS} names.`);
      return false;
    }

    // No errors found.
    setError(undefined);
    return true;
  };

  const onAppendParticipant = () => {
    // Don't allow appending to go above the maximum number of participants
    if (participants.length === MAXIMUM_PARTICIPANTS) {
      return;
    }
    const newParticipantData = newParticipant();
    setParticipants(prev => [...prev, newParticipantData]);
    setFocusParticipantId(newParticipantData.id);
    setError(undefined);
  };

  const onInsertParticipant = (index: number) => {
    // Don't allow inserting to go above the maximum number of participants
    if (participants.length === MAXIMUM_PARTICIPANTS) {
      return;
    }
    const newParticipantData = newParticipant();
    setParticipants(prev => [...prev.slice(0, index), newParticipantData, ...prev.slice(index)]);
    setFocusParticipantId(newParticipantData.id);
    setError(undefined);
  };

  const onDeleteParticipant = (id: string) => {
    // Don't allow deleting to go below the minimum number of participants
    if (participants.length === MINIMUM_PARTICIPANTS) {
      return;
    }
    const currentIndex = participants.findIndex(p => p.id === id);
    const newParticipants = removeInvalidExclusions(participants.filter(p => p.id !== id));
    setParticipants(newParticipants);
    setError(undefined);
    // Focus on the previous input, or the first one if we deleted the first
    if (newParticipants.length > 0) {
      const targetIndex = Math.max(0, currentIndex - 1);
      const targetParticipant = newParticipants[targetIndex];
      if (targetParticipant) {
        setFocusParticipantId(targetParticipant.id);
      }
    }
  };

  const onUpdateParticipantName = (id: string, name: string) => {
    setParticipants(prev => {
      const updated = prev.map(p => (p.id === id ? { ...p, name } : p));
      // Auto-append a new empty input when the user starts typing in the last field
      const isLastParticipant = updated[updated.length - 1]?.id === id;
      if (isLastParticipant && name.trim() !== '' && updated.length < MAXIMUM_PARTICIPANTS) {
        return [...updated, newParticipant()];
      }
      return updated;
    });
    setError(undefined);
  };

  const onUpdateParticipantExclusions = (id: string, exclusions: string[]) => {
    setParticipants(prev => prev.map(p => (p.id === id ? { ...p, exclusions } : p)));
    setError(undefined);
  };

  const clearEmptyParticipants = () => {
    setParticipants(prev => removeInvalidExclusions(prev.filter(isNonEmptyParticipant)));
  };

  return {
    participants,
    validationError: error,
    duplicateParticipantIds,
    isParticipantsEmpty,
    focusParticipantId,
    validateParticipants,
    onAppendParticipant,
    onInsertParticipant,
    onDeleteParticipant,
    onUpdateParticipantName,
    onUpdateParticipantExclusions,
    clearEmptyParticipants,
  };
}

export function newParticipant(): Participant {
  return {
    id: crypto.randomUUID(),
    name: '',
    exclusions: [],
  };
}

export function initParticipants(): Participant[] {
  return Array.from({ length: MINIMUM_PARTICIPANTS })
    .fill(0)
    .map(() => newParticipant());
}

function isNonEmptyParticipant(participant: Participant): boolean {
  return participant.name.trim() !== '';
}

function getDuplicateParticipantIds(participants: Participant[]): Set<string> {
  const nameCounts = new Map<string, number>();
  for (const { name } of participants) {
    const trimmed = name.trim();
    nameCounts.set(trimmed, (nameCounts.get(trimmed) ?? 0) + 1);
  }
  // Return a set of all ids that have name counts of more than 0
  return new Set(participants.filter(p => (nameCounts.get(p.name.trim()) ?? 0) > 1).map(p => p.id));
}

function removeInvalidExclusions(participants: Participant[]): Participant[] {
  const validParticipantIds = new Set(participants.map(participant => participant.id));

  return participants.map(participant => ({
    ...participant,
    exclusions: participant.exclusions.filter(
      exclusionId => exclusionId !== participant.id && validParticipantIds.has(exclusionId),
    ),
  }));
}
